import React, { useState } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { device, size } from "../../constants";
import "swiper/css";
import { useTranslation } from "next-i18next";
import { useWindowSize } from "../../hooks/useWindowResize";
import { Button } from "../../pages/[locale]/casos/components/Button";
import { SlideArrowNavigation } from "../blog/RelatedPosts/SlideArrowNavigation";
import { SlideBulletNavigation } from "../blog/RelatedPosts/SlideBulletNavigation";
import { HighlightBar } from "./HighlightBar";

const Section = styled.section`
  background: ${({ theme }) => theme.colors["neutral-07"]};
  text-align: center;

  .bar > div {
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  margin: 16px 0;
  color: ${({ theme }) => theme.colors["neutral-01"]};
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
`;

const Paragraph = styled.p`
  font-weight: 400;
  font-size: 18px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
  color: ${({ theme }) => theme.colors["neutral-01"]};
	max-width: 1062px;
	margin: 0 auto;

	@media ${device.laptopM} {
		font-size: 20px;
		line-height: 36px;
	}
`;

const NavigationContainer = styled.div`
  margin-top: 40px;
	
	.arrow-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		height: 100%;
		position: relative;
	}

	.arrow-left, .arrow-right {
		width: 40px;
		height: 40px;

		&:is([data-theme="dark"] &:disabled) {
			background-color: ${({ theme }) => theme.colors["neutral-07"]};
		}
	}

	@media ${device.tablet} {
		margin-top: 0;
		position: absolute;
		inset: 0;
		z-index: 9;
	}
`;

const SwiperWrapper = styled.div`
  margin-top: 40px;

  .bullets-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 40px;
  }

  .bullet {
    background-color: var(--product-color);
    opacity: 0.5;
  }
  
  .bullet-active {
    opacity: 1;
  }

  :is([data-theme="dark"] &) {
    .bullet {
      background-color: var(--product-color);
    }
  }
`;

const SlideContent = styled.div`
  border-radius: 8px;
  position: relative;
  margin: 0 auto;
  height: 848px;
  
  @media ${device.tablet} {
    height: 340px;
    max-width: 540px;
  }
  
	@media ${device.laptop} {
    height: 504px;
    max-width: 780px;
  }
	
	@media ${device.laptopM} {
    height: 504px;
    max-width: 1120px;
  }
`;

const SlideImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 1;
`;

const SlideTextBox = styled.div`
`;

const StepNumber = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 8px;
`;

const StepText = styled.div`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 400;
  max-width: 700px;
`;

export default function HowItWorksSection({ title, description, steps }) {
	const { width } = useWindowSize();
	const isMobile = width <= size.laptop;
	const { t } = useTranslation();

	const [activeBullet, setActiveBullet] = useState(0);

	return (
		<Section>
			<div className="container-blog">
				<div className="bar">
					<HighlightBar />
				</div>
				<Title dangerouslySetInnerHTML={{ __html: title }} />
				<Paragraph dangerouslySetInnerHTML={{ __html: description }} />

				<SwiperWrapper>
					<Swiper
						spaceBetween={40}
						slidesPerView={1}
						navigation={false}
						onSlideChange={(e) => setActiveBullet(e.activeIndex)}
					>
						{steps.map((step, idx) => (
							<SwiperSlide key={step.name}>
								<SlideContent>
									<SlideImage
										src={step.image}
										alt={step.name}
										aria-hidden="true"
									/>
									<SlideTextBox className="sr-only">
										<StepNumber>
											<span>{idx + 1}.</span>{" "}
											<strong style={{ fontWeight: 700 }}>
												{step.name.toUpperCase()}
											</strong>
										</StepNumber>
										<StepText>{step.text}</StepText>
									</SlideTextBox>
								</SlideContent>
							</SwiperSlide>
						))}

						<NavigationContainer>
							{width >= size.tablet && <SlideArrowNavigation />}
							{width < size.tablet && (
								<SlideBulletNavigation activeBullet={activeBullet} />
							)}
						</NavigationContainer>
					</Swiper>
				</SwiperWrapper>

				<Button
					as="button"
					className="mt-14 text-neutral-08 bg-vivid-04 uppercase tracking-[0.6px] w-full max-w-[342px] laptop:px-[19.5px] laptop:min-w-[532px] mx-auto"
					onClick={() => {
						const contactForm = document.getElementById("contact-form");
						contactForm.scrollIntoView({
							behavior: "smooth",
							block: "center",
						});
					}}
				>
					{t("products.rpa.buttons.developInMyCompany")}
				</Button>
			</div>
		</Section>
	);
}
